import React, { FC, useRef, useEffect } from 'react';
import { colors } from 'constants/colors';
import BlockContainer from 'Components/landings/BlockContainer';
import styled from 'styled-components';
import { Box, Flex } from '@igooods/ui-kit';
import Slider, { EnumArrowType, SliderWrapper } from 'Components/Slider';
import BananaWEBM from './assets/banana.webm';
import PepperWEBM from './assets/pepper.webm';
import FishWEBM from './assets/fish.webm';
import BananaImg from './assets/banana-slider.jpg';
import PepperImg from './assets/pepper-slider.jpg';
import FishImg from './assets/fish-slider.jpg';
import Flickity from 'react-flickity-component';
import { mediaMdDown, mediaSmDown, mediaSmUp, mediaXlDown, mediaCustomDown, mediaXlUp } from 'theme/helpers/css';
import { FontFamily } from 'theme';
import { Transitions } from 'theme';
import SelectIcon from 'Components/Icon/SelectIcon';

const Legend = styled(Box).attrs({
  pt: { sm: 20, lg: 30 },
  mb: { xs: 10, sm: 0 },
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;

  ${mediaSmDown`
    position: relative;
  `}
`;

const Content = styled(Box).attrs({ pb: { xs: 53, sm: 0 }, pt: { xs: 12, sm: 0 } })`
  position: relative;
`;

const Property = styled(Flex).attrs({
  column: true,
})`
  width: 25%;
  padding: 0 14px;
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

  ${mediaMdDown`
    padding-right: 10px;
    padding-left: 10px;
  `}

  ${mediaSmDown`
    width: 100%;
    flex-direction: row;

    & + & {
      margin-top: 24px;
    }
  `}
`;

const VideoLayout = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: linear-gradient(
    to right,
    ${colors.heliotrope},
    rgba(221, 84, 248, 0) 20%,
    rgba(221, 84, 248, 0) 80%,
    ${colors.heliotrope}
  );
  z-index: 1;

  ${mediaXlUp`
    background: linear-gradient(to right, ${colors.heliotrope}, transparent 40%, transparent 60%, ${colors.heliotrope});
  `}

  ${mediaSmDown`
    display: none;
  `}
`;

const VideoContainer = styled.div`
  height: 800px;
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;

  ${mediaMdDown`
    height: 483px;
  `}

  ${mediaSmDown`
    height: 440px;
  `}
`;

const Video = styled(Flex).attrs({
  center: true,
})`
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Slide = styled(Flex).attrs({
  column: true,
})`
  width: 100%;
  left: 0 !important;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;

  ${VideoContainer} {
    transform: translateY(-10px);
  }

  ${Property} {
    transform: translateY(-10px);
    opacity: 0;
  }

  ${mediaSmUp`
    overflow: hidden;
  `}

  ${mediaSmDown`
    background-color: ${colors.heliotrope};
  `}

  &.is-selected {
    opacity: 1;
    transition-duration: 0.4s;

    ${VideoContainer} {
      transform: translateY(0);
      transition-duration: 0.5s;
    }

    ${Property} {
      transform: translateY(0);
      transition-duration: 0.6s;
      opacity: 1;

      &:nth-child(2) {
        transition-duration: 0.9s;
      }
      &:nth-child(3) {
        transition-duration: 1.2s;
      }
      &:nth-child(4) {
        transition-duration: 1.5s;
      }
    }
  }
`;

export const LegendItem = styled.div`
  font-size: 120px;
  line-height: 120px;
  color: ${colors.white};
  opacity: 0.2;
  font-family: ${FontFamily.secondFont};
  font-weight: 700;
  transition: ${Transitions.opacity};
  padding-right: 1em;
  cursor: pointer;

  &.is-selected {
    opacity: 1;
  }

  ${mediaXlDown`
    font-size: 96px;
    line-height: 96px;
  `}

  ${mediaMdDown`
    font-size: 64px;
    line-height: 64px;
    padding-right: 32px;
  `}

  ${mediaCustomDown(540)`
    font-size: 44px;
    line-height: 54px;
    padding-right: 16px;;
  `}
`;

const Title = styled.div`
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${colors.white};

  ${mediaMdDown`
    font-size: 12px;
    line-height: 16px;
  `}
`;

const SliderContainer = styled(Box)`
  width: 100%;
  z-index: 1;

  & .flickity-slider {
    transform: none !important;
  }

  & .flickity-button {
    top: 372px !important;
    z-index: 2;

    ${mediaXlDown`
      top: 346px !important;
    `}

    ${mediaMdDown`
      top: 232px !important;
    `}

    ${mediaSmDown`
      top: 4px !important;
    `}
  }

  & .flickity-prev-next-button.next {
    left: 86px;
    right: auto !important;

    ${mediaMdDown`
      left: 60px;
    `}
  }
`;

const ProductProperties = styled(Flex)`
  margin: 0 -14px;
  position: absolute;
  bottom: 56px;
  left: 0;
  z-index: 2;

  ${mediaMdDown`
    margin-left: -10px;
    margin-right: -10px;
    bottom: 32px;
  `}

  ${mediaSmDown`
    flex-direction: column;
    bottom: -150px;
  `}
`;

const PropertyText = styled.div`
  font-size: 14px;
  line-height: 26px;
  color: ${colors.white};
  margin-top: 20px;

  ${mediaMdDown`
    font-size: 12px;
    line-height: 18px;
    margin-top: 12px;
  `}

  ${mediaSmDown`
    margin-left: 10px;
    margin-top: 0;
  `}

  svg {
    width: 11px !important;
    height: auto !important;
  }
`;

const StyledSlider = styled(Slider)`
  ${SliderWrapper} {
    overflow: visible !important;
  }
`;

const Select = styled(SelectIcon)`
  flex: 0 0 auto;

  ${mediaMdDown`
    width: 11px;
    height: auto;
  `}

  ${mediaSmDown`
    align-self: flex-start;
    margin-top: 5px;
  `}
`;

const VideoFile = styled.video`
  ${mediaSmDown`
    display: none;
  `}
`;

const VideoPlaceholder = styled.img`
  width: auto;
  height: 100%;
  position: absolute;
  top: 0;
  left: 50%;
  z-index: -1;
  transform: translateX(-50%);
`;

const FishVideoPlaceholder = styled(VideoPlaceholder)`
  ${mediaSmDown`
    width: 140vw;
    height: auto;
    top: 50%;
    transform: translate(-50%, -60%);
  `}
`;

const SelectionBlock: FC = () => {
  const flickityRef = useRef<Flickity>(null);
  const legendRef = useRef<Flickity>(null);
  const secondSlideVideoRef = useRef(null);

  useEffect(() => {
    if (flickityRef.current && legendRef.current) {
      flickityRef.current.on('change', (idx: number) => {
        legendRef.current?.select(idx);
      });
    }
  }, [legendRef, flickityRef]);

  useEffect(() => {
    if (flickityRef.current && legendRef.current) {
      legendRef.current.on('change', (idx: number) => {
        flickityRef.current?.select(idx);
      });
    }
  }, [legendRef, flickityRef]);

  useEffect(() => {
    if (flickityRef.current && legendRef.current) {
      legendRef.current.on('staticClick', (...args: any[]) => {
        flickityRef.current?.select(args[3]);
      });
    }
  }, [legendRef, flickityRef]);

  useEffect(() => {
    //нужно, чтобы размеры слайдов пересчитались после того, как загрузился не дефолтный шрифт (шрифт другой -> размеры другие)
    if (legendRef.current) {
      window.addEventListener('load', () => {
        legendRef.current?.resize();
      });
    }
  }, [legendRef]);

  return (
    <BlockContainer bgColor={colors.heliotrope}>
      <Content>
        <Legend>
          <Title>Вот так пикеры проверяют</Title>
          <Box mt={{ xs: 2, sm: 4.5 }} ml={{ lg: -2.5, xl: -3 }}>
            <StyledSlider ref={legendRef} infinite>
              <LegendItem>Бананы</LegendItem>
              <LegendItem>Перцы</LegendItem>
              <LegendItem>Рыбу</LegendItem>
            </StyledSlider>
          </Box>
        </Legend>
        <SliderContainer>
          <Slider withArrows ref={flickityRef} infinite arrowType={EnumArrowType.transparent}>
            <Slide>
              <VideoLayout />
              <VideoContainer>
                <Video>
                  <VideoPlaceholder src={BananaImg} alt="🍌" data-loading="lazy" />
                  <VideoFile height="100%" width="auto" autoPlay muted loop playsinline poster={BananaImg}>
                    <source
                      src="https://72ec245e-7b58-4b71-a948-3c95cd185228.selcdn.net/static/static/banana.b951f89c..mp4"
                      type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
                    />
                    <source src={BananaWEBM} type='video/webm; codecs="vp8, vorbis"' />
                  </VideoFile>
                </Video>
              </VideoContainer>
              <ProductProperties>
                <Property>
                  <Select />
                  <PropertyText>
                    Кожура должна быть матовой и&nbsp;гладкой, а&nbsp;форма&nbsp;&mdash; обтекаемой, без ребристости.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>
                    Черные точки&nbsp;&mdash; это безопасно. Дозрел! Черные пятна&nbsp;&mdash; не&nbsp;надо, гниет.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>
                    Слегка зеленоватый банан — еще не поспел. Позвонить клиенту и уточнить, подойдет или нет.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>Серый&nbsp;&mdash; не&nbsp;брать. Подморожен.</PropertyText>
                </Property>
              </ProductProperties>
            </Slide>
            <Slide>
              <VideoLayout />
              <VideoContainer>
                <Video>
                  <VideoPlaceholder src={PepperImg} alt="🌶" data-loading="lazy" />
                  <VideoFile
                    height="100%"
                    width="auto"
                    muted
                    loop
                    autoPlay
                    playsinline
                    poster={PepperImg}
                    ref={secondSlideVideoRef}
                  >
                    <source
                      src="https://72ec245e-7b58-4b71-a948-3c95cd185228.selcdn.net/static/static/pepper.16ece13b..mp4"
                      type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
                    />
                    <source src={PepperWEBM} type='video/webm; codecs="vp8, vorbis"' />
                  </VideoFile>
                </Video>
              </VideoContainer>
              <ProductProperties>
                <Property>
                  <Select />
                  <PropertyText>
                    Должен быть твердым на&nbsp;ощупь, а&nbsp;кожура&nbsp;&mdash; яркой и&nbsp;однотонной.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>Черные точки и&nbsp;пятна&nbsp;&mdash; не&nbsp;берем. Начал подгнивать.</PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>
                    Хвостик&nbsp;&mdash; зеленый и&nbsp;упругий. Черный и&nbsp;засохший&nbsp;&mdash; не&nbsp;покупать.
                    Перец залежался.
                  </PropertyText>
                </Property>
              </ProductProperties>
            </Slide>
            <Slide>
              <VideoLayout />
              <VideoContainer>
                <Video>
                  <FishVideoPlaceholder src={FishImg} alt="🐟" data-loading="lazy" />
                  <VideoFile height="100%" width="auto" muted loop autoPlay playsinline poster={FishImg}>
                    <source
                      src="https://72ec245e-7b58-4b71-a948-3c95cd185228.selcdn.net/static/static/fish.46f43088..mp4"
                      type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
                    />
                    <source src={FishWEBM} type='video/webm; codecs="vp8, vorbis"' />
                  </VideoFile>
                </Video>
              </VideoContainer>
              <ProductProperties>
                <Property>
                  <Select />
                  <PropertyText>
                    Жабры должны быть красными или ярко-розовыми, а&nbsp;глаза&nbsp;&mdash; выпуклыми и&nbsp;ясными.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>Чешуя блестит и&nbsp;без пятен&nbsp;&mdash; хорошо.</PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>
                    Резко и&nbsp;неприятно пахнет&nbsp;&mdash; не&nbsp;покупать. Рыба испортилась.
                  </PropertyText>
                </Property>
                <Property>
                  <Select />
                  <PropertyText>
                    Если надавить пальцами на&nbsp;брюшко&nbsp;&mdash; не&nbsp;должно остаться ямок.
                  </PropertyText>
                </Property>
              </ProductProperties>
            </Slide>
          </Slider>
        </SliderContainer>
      </Content>
    </BlockContainer>
  );
};

export default SelectionBlock;
